"use client";

import type { ComponentProps } from "react";

import { DayPicker } from "react-day-picker";
import { DayPicker as JalaliDayPicker } from "react-day-picker/jalali";

import { cn } from "@/shared/libs/commonUtils";
import { buttonVariants } from "./button";
import { faIR } from "date-fns/locale";
import { BeerStein, CalendarDot } from "@phosphor-icons/react";

export type CalendarProps = ComponentProps<typeof DayPicker>;

function Calendar({
  className,
  classNames,
  showOutsideDays = true,
  locale,
  ...props
}: CalendarProps) {
  const DaysPicker = locale == faIR ? JalaliDayPicker : DayPicker;

  return (
    <DaysPicker
      dir={locale == faIR ? "rtl" : "ltr"}
      showOutsideDays={showOutsideDays}
      className={cn("p-3", className)}
      classNames={{
        weekday:
          "text-muted-foreground rounded-md w-9 font-normal text-[0.8rem]",
        months: "flex flex-col  md:flex-row gap-y-4 sm:space-x-4 sm:gap-y-0",
        month: "gap-y-4 ",
        month_caption: "flex justify-center pt-1 relative items-center",
        caption_label: "text-sm font-medium",
        nav: "space-x-1 flex items-center",
        button_previous: `hover:bg-primary-foreground md:top-20 md:z-10 ${
          locale == faIR ? "absolute right-11" : "absolute left-11 rotate-180"
        }`,
        button_next: `hover:bg-primary-foreground md:top-20 md:z-10 ${
          locale == faIR ? "absolute left-11 rotate-180" : "absolute right-11 "
        }`,
        table: "w-full border-collapse gap-y-1",
        weekdays: "!mb-4 h-9 w-9",
        head: "flex w-full mt-2",
        day: " text-center text-sm p-0 relative [&:has([aria-selected].day-range-end)]:rounded-r-md [&:has([aria-selected].day-outside)]:bg-muted dark:bg-muted/10/50 [&:has([aria-selected])]:bg-muted dark:bg-muted/10 first:[&:has([aria-selected])]:rounded-l-md last:[&:has([aria-selected])]:rounded-r-md focus-within:relative focus-within:z-20",
        day_button: cn(
          buttonVariants({ variant: "ghost" }),
          "h-9 w-9 p-0 font-normal aria-selected:opacity-100",
        ),
        range_end: "day-range-end",
        selected:
          "bg-primary text-background hover:bg-primary! hover:text-primary-foreground focus:bg-primary focus:text-background",
        today: "bg-muted dark:bg-muted/10 text-accent-foreground",
        outside:
          "day-outside text-muted-foreground opacity-50 aria-selected:bg-muted dark:bg-muted/10/50 aria-selected:text-muted-foreground aria-selected:opacity-30",
        disabled: "text-muted-foreground opacity-50 cursor-not-allowed!",
        range_middle:
          "aria-selected:bg-muted dark:bg-muted/10 aria-selected:text-accent-foreground",
        hidden: "invisible",
        ...classNames,
      }}
      components={{
        Button: ({ ...props }) => <CalendarDot className="h-4 w-4" />,
      }}
      {...props}
    />
  );
}
Calendar.displayName = "Calendar";

export { Calendar };
